import React, { useState, useCallback, useMemo } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import SaveChatSessionDialog from '../components/chat/SaveChatSessionDialog';

export const useChatActions = ({
    sessionId,
    chatSessions,
    updateSessionMetadata,
    resetState,
    reloadChatSessionList,
}) => {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
    const [sessionToSaveDialogData, setSessionToSaveDialogData] = useState(null);
    const [isSavingMetadata, setIsSavingMetadata] = useState(false);
    const [error, setError] = useState(null);

    const handleNewSessionRequest = useCallback(() => {
        setConfirmOpen(true);
    }, []);

    const handleConfirmNewSession = useCallback(async () => {
        setConfirmOpen(false);
        if (resetState) {
            await resetState(true);
        } else {
            setError("Failed to start new session: context error.");
            return;
        }
        if (reloadChatSessionList) reloadChatSessionList(false);
    }, [resetState, reloadChatSessionList]);

    const handleCancelNewSession = useCallback(() => {
        setConfirmOpen(false);
    }, []);

    const currentSessionDisplayInfo = useMemo(() => {
        if (!sessionId) return null;
        return chatSessions.find(s => s.id === sessionId);
    }, [sessionId, chatSessions]);

    const handleOpenSaveDialog = useCallback(() => {
        if (!sessionId) {
            setError("Cannot save: No active session.");
            return;
        }
        const currentSessionFromList = chatSessions.find(s => s.id === sessionId);
        setSessionToSaveDialogData(currentSessionFromList || { id: sessionId, name: '', save: false });
        setIsSaveDialogOpen(true);
    }, [sessionId, chatSessions]);

    const handleCloseSaveDialog = useCallback(() => {
        setIsSaveDialogOpen(false);
    }, []);

    const handleConfirmSaveSession = useCallback(async (name) => {
        if (!sessionToSaveDialogData || !sessionToSaveDialogData.id) {
            setError("Error: Session data is missing for save operation.");
            return;
        }
        setIsSavingMetadata(true);
        setError(null);
        try {
            await updateSessionMetadata(sessionToSaveDialogData.id, { name, save: true });
            handleCloseSaveDialog();
            const wasPreviouslyUnsavedOrUnknown = !chatSessions.find(s => s.id === sessionId)?.save;
            if (wasPreviouslyUnsavedOrUnknown) {
                reloadChatSessionList(false);
            }
        } catch (err) {
            console.error("Error saving session metadata:", err);
            setError(`Failed to save session: ${err.message}`);
        } finally {
            setIsSavingMetadata(false);
        }
    }, [sessionToSaveDialogData, updateSessionMetadata, handleCloseSaveDialog, reloadChatSessionList, chatSessions, sessionId]);

    const renderDialogs = () => (
        <>
            <Dialog
                open={confirmOpen}
                onClose={handleCancelNewSession}
                aria-labelledby="confirm-new-session-dialog-title"
            >
                <DialogTitle id="confirm-new-session-dialog-title">Start New Session?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Starting a new session will clear the current chat history and agent state. Are you sure you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelNewSession}>Cancel</Button>
                    <Button onClick={handleConfirmNewSession} color="primary" autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
            <SaveChatSessionDialog
                open={isSaveDialogOpen}
                onClose={handleCloseSaveDialog}
                onSave={handleConfirmSaveSession}
                session={sessionToSaveDialogData}
                isSaving={isSavingMetadata}
            />
        </>
    );

    return {
        error,
        setError,
        currentSessionDisplayInfo,
        onNewSessionRequest: handleNewSessionRequest,
        onSaveSessionRequest: handleOpenSaveDialog,
        renderDialogs,
    };
};